import type {
  Appearance,
  StripeElementsUpdateOptions as BaseStripeElementsUpdateOptions,
  FieldOption,
  FieldsOption,
  TermOption,
  TermsOption,
} from "@stripe/stripe-js";

// General
// ==============================================

export interface StripeElementsAddress {
  city: string;
  country: string;
  line1: string;
  line2?: string;
  postal_code: string;
  state: string;
}

// Elements
// ==============================================

export const stripeElementsModes = {
  setup: "setup",
  payment: "payment",
  subscription: "subscription",
} as const;
export type StripeElementsMode = keyof typeof stripeElementsModes;

export const stripeElementsSetupFutureUsages = {
  off_session: "off_session",
  on_session: "on_session",
} as const;
export type StripeElementsSetupFutureUsage =
  keyof typeof stripeElementsSetupFutureUsages;

// https://docs.stripe.com/js/elements_object/create_without_intent#stripe_elements_no_intent-options
export interface StripeElementsOptions {
  amount: number;
  appearance?: Appearance;
  currency: string;
  customerSessionClientSecret?: string;
  mode: StripeElementsMode;
  paymentMethodConfiguration?: string;
  setupFutureUsage?: StripeElementsSetupFutureUsage;
}

// https://docs.stripe.com/js/elements_object/update
export type StripeElementsUpdateOptions = BaseStripeElementsUpdateOptions & {
  customerSessionClientSecret?: string | null;

  // Our stripe.js version is outdated and expects these parameters in snake_case
  payment_method_configuration?: string | null;
  payment_method_types?: string[] | null;
  setup_future_usage?: StripeElementsSetupFutureUsage | null;
};

export interface StripeElements {
  off: (
    event: "update-end",
    listener: () => Promise<void> | (() => void),
  ) => void;

  // https://docs.stripe.com/js/element/events/on_update_end
  on: (
    event: "update-end",
    listener: () => Promise<void> | (() => void),
  ) => void;
  // https://docs.stripe.com/js/elements_object/update
  update: (options: StripeElementsUpdateOptions) => void;
}

// Payment Element
// ==============================================

export const stripeElementsPaymentElementLayoutTypes = {
  accordion: "accordion",
  tabs: "tabs",
} as const;

export type StripeElementsPaymentElementLayoutType =
  keyof typeof stripeElementsPaymentElementLayoutTypes;

export interface StripeElementsPaymentElementOptions {
  // https://docs.stripe.com/js/elements_object/create_payment_element#payment_element_create-options-defaultValues
  defaultValues: {
    billingDetails: Partial<{
      email?: string;
      name?: string;
      phone?: string;
      address?: Partial<StripeElementsAddress>;
    }>;
  };
  fields: FieldsOption;
  // https://docs.stripe.com/js/elements_object/create_payment_element#payment_element_create-options-layout
  layout: {
    type: StripeElementsPaymentElementLayoutType;
    radios: boolean;
  };
  // https://docs.stripe.com/js/elements_object/create_payment_element#payment_element_create-options-terms
  terms: TermsOption & {
    googlePay: TermOption;
    applePay: TermOption;
  };
}

// Address Element
// ==============================================

export const stripeElementsAddressElementModes = {
  billing: "billing",
  shipping: "shipping",
} as const;
export type StripeElementsAddressElementMode =
  keyof typeof stripeElementsAddressElementModes;

export interface StripeElementsAddressElementOptions {
  defaultValues: {
    name?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    address?: Partial<StripeElementsAddress>;
  };
  display: {
    name: "full" | "short" | "none";
  };
  fields: {
    phone: FieldOption;
  };
  mode: StripeElementsAddressElementMode;
}

// To be removed once PMC becomes the default

export interface StripeElementAddressObject {
  city: string;
  country: string;
  line1: string;
  line2?: string;
  postal_code: string;
  state: string;
}

// https://stripe.com/docs/js/element/events/on_change?type=addressElement
export interface OnStripeElementAddressChangeEvent {
  complete: boolean;
  elementMode: "billing";
  elementType: "address";
  empty: boolean;
  isNewAddress: boolean;
  value: {
    name: string;
    firstName?: string;
    lastName?: string;
    address: StripeElementAddressObject;
    phone?: string;
  };
}
